/** @prettier */
import React, { ReactNode, useCallback, useMemo, useState } from "react";
import moment from "moment";
import ReactTable, { Column } from "react-table";
import { Button, Label, ButtonGroup } from "react-bootstrap";
import { LinkButton } from "../util/LinkButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Pagination from "../CommonComponents/Pagination";
import { getStatusBsStyle, getStatusDisplayString } from "../util/statusUtils";
import AssignAuthorModal, { AssignAuthorModalState } from "./AssignAuthorModal";
import { PageParams, Subscription, User } from "../services/qbankApi";
import AssignSelfModal from "./AssignSelfModal";
import { AuthorData } from "./authorsApi";
import * as authorsApi from "./authorsApi";
import { Question } from "../Question/questionApi";
import * as questionApi from "../Question/questionApi";

const PAGE_SIZE = 25;

const buildColumns = (
    actionsCell: (questionData: Question) => ReactNode,
): Array<Column<Question>> => [
    {
        id: "created",
        Header: "Created",
        accessor: "createdDate",
        Cell: (row) => moment(row.value).format("YYYY-MM-DD"),
    },
    {
        id: "updated",
        Header: "Modified",
        accessor: "modifiedDate",
        Cell: (row) => moment(row.value).format("YYYY-MM-DD"),
    },
    {
        id: "subject",
        Header: "Subject",
        accessor: "subject.name",
    },
    {
        id: "category",
        Header: "Category",
        accessor: "category.name",
    },
    {
        id: "subcategory",
        Header: "Subcategory",
        accessor: "subcategory.name",
    },
    {
        Header: "Learning Objective",
        accessor: "learningObjective",
        sortable: false,
    },
    {
        id: "status",
        Header: "Status",
        accessor: "status",
        Cell: (row) => (
            <Label bsStyle={getStatusBsStyle(row.value)}>
                {getStatusDisplayString(row.value)}
            </Label>
        ),
    },
    {
        id: "comments",
        Header: "Comments",
        filterable: false,
        sortable: false,
        accessor: ({ commentList }) => {
            const unreadComments = commentList.filter((x) => x.read);
            const labelStyle = unreadComments.length ? "danger" : "default";
            return (
                <div>
                    {!!commentList.length && (
                        <Label bsStyle={labelStyle}>{commentList.length}</Label>
                    )}
                    {!commentList.length && "-"}
                </div>
            );
        },
    },
    {
        id: "author",
        Header: "Author",
        sortable: false,
        accessor: ({ author }) => (author ? author.fullName : "Unassigned"),
    },
    {
        id: "actions",
        Header: "Actions",
        filterable: false,
        sortable: false,
        accessor: actionsCell,
    },
];

function resetTableScroll() {
    // Used to fix the table scroll on pagination change
    // https://github.com/tannerlinsley/react-table/issues/772
    const tableEl = document.getElementsByClassName("rt-tbody")[0];
    if (tableEl) {
        tableEl.scrollTop = 0;
    }
}

type ModalState =
    | ({ kind: "assignAuthor" } & AssignAuthorModalState)
    | { kind: "assignSelf"; assignSelf: () => void };

type QuestionListProps = {
    role: "auditor" | "writer";
    me: User;
    activeSubscription: Subscription;
};
const QuestionList = ({ role, me, activeSubscription }: QuestionListProps) => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState(true);
    const [pageCount, setPageCount] = useState(0);
    const [modalState, setModalState] = useState<ModalState>();

    const closeModal = () => setModalState(undefined);

    const fetchData = useCallback(
        (state: {
            page: number;
            sorted?: Array<{ id: string; desc: boolean }>;
        }) => {
            setLoading(true);

            let sortParams: Partial<PageParams> = state.sorted
                ? {
                      sortField: state.sorted[0].id,
                      sortOrder: state.sorted[0].desc ? "DESC" : "ASC",
                  }
                : {};

            let params = {
                page: state.page,
                size: PAGE_SIZE,
                ...sortParams,
            };
            const getQuestions =
                role === "auditor"
                    ? questionApi.getProductQuestions
                    : questionApi.getUserQuestions;

            getQuestions(activeSubscription.product.uuid, params)
                .catch((error) => {
                    console.log(error);
                    return { content: [], totalPages: 0 };
                })
                .then(({ content, totalPages }) => {
                    setQuestions(content);
                    setLoading(false);
                    setPageCount(totalPages);
                    resetTableScroll();
                });
        },
        [activeSubscription.product.uuid, role],
    );

    const changeAuthor = useCallback(
        (questionUuid: string, author?: AuthorData) => {
            if (!questions) return;
            const newAuthor = author && {
                uuid: author.userUuid,
                username: author.username,
                fullName: author.fullName,
            };
            setQuestions(
                questions.map((q) =>
                    q.uuid === questionUuid ? { ...q, author: newAuthor } : q,
                ),
            );
        },
        [questions],
    );

    const productUuid = activeSubscription.product.uuid;
    const beginAssignQuestion = useCallback(
        (questionUuid: string) => {
            const assignAuthor = async (
                questionUuid: string,
                author: AuthorData,
            ) => {
                await authorsApi.assignAuthor(questionUuid, author.userUuid);
                changeAuthor(questionUuid, author);
            };

            setModalState(
                role === "auditor"
                    ? {
                          kind: "assignAuthor",
                          questionUuid,
                          me,
                          productUuid,
                          assignAuthor,
                      }
                    : {
                          kind: "assignSelf",
                          assignSelf: () =>
                              assignAuthor(questionUuid, {
                                  fullName: `${me.firstName} ${me.lastName}`,
                                  userUuid: me.uuid,
                                  username: me.username,
                              }),
                      },
            );
        },
        [role, changeAuthor, me, productUuid],
    );

    const columns = useMemo(
        () =>
            buildColumns(({ uuid, status, author, versionInfo }) => {
                const isEditorOrAuthor =
                    activeSubscription.questionAuditor ||
                    author?.uuid === me.uuid;
                const isLatestVersion =
                    !versionInfo ||
                    versionInfo.versionNum === versionInfo.latestVersionNum;
                return (
                    <ButtonGroup bsSize="xsmall">
                        {status !== "DRAFT" && (
                            <LinkButton
                                title="View"
                                to={"/questions/" + uuid + "/view"}
                            >
                                <FontAwesomeIcon icon="eye" />
                            </LinkButton>
                        )}
                        {status !== "DELETED" && (
                            <>
                                {isEditorOrAuthor && isLatestVersion && (
                                    <LinkButton
                                        title="Edit"
                                        to={"/questions/" + uuid + "/edit"}
                                    >
                                        <FontAwesomeIcon icon="edit" />
                                    </LinkButton>
                                )}
                                {author ? (
                                    <Button
                                        bsStyle="link"
                                        title="Unassign"
                                        onClick={async () => {
                                            await authorsApi.unassignAuthor(
                                                uuid,
                                                author.uuid,
                                            );
                                            changeAuthor(uuid, undefined);
                                        }}
                                    >
                                        <FontAwesomeIcon icon="user-minus" />
                                    </Button>
                                ) : (
                                    <Button
                                        bsStyle="link"
                                        title="Assign"
                                        onClick={() =>
                                            beginAssignQuestion(uuid)
                                        }
                                    >
                                        <FontAwesomeIcon icon="user-plus" />
                                    </Button>
                                )}
                            </>
                        )}
                        {(versionInfo?.versionNum ?? 1) > 1 && (
                            <Button
                                disabled
                                bsStyle="link"
                                title="Has previous version"
                            >
                                <FontAwesomeIcon icon="history" />
                            </Button>
                        )}
                    </ButtonGroup>
                );
            }),
        [beginAssignQuestion, changeAuthor, activeSubscription, me],
    );

    return (
        <>
            <ReactTable
                columns={columns}
                PaginationComponent={Pagination}
                defaultPageSize={PAGE_SIZE}
                defaultSorted={[
                    {
                        id: "updated",
                        desc: true,
                    },
                ]}
                getProps={() => {
                    return { style: { height: "100%" } };
                }}
                getTheadThProps={() => {
                    return { style: { fontSize: "17px" } };
                }}
                className="-striped -highlight"
                data={questions}
                loading={loading} // Display the loading overlay when we need it
                manual
                multiSort={false}
                onFetchData={fetchData}
                pages={pageCount}
                pageSize={PAGE_SIZE}
                resizable={false}
            />
            {modalState?.kind === "assignAuthor" && (
                <AssignAuthorModal closeModal={closeModal} {...modalState} />
            )}
            {modalState?.kind === "assignSelf" && (
                <AssignSelfModal closeModal={closeModal} {...modalState} />
            )}
        </>
    );
};

export default QuestionList;
